import React, { createContext, useCallback, useContext, useMemo } from 'react';
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { InferType } from 'yup';

import { FunctionComponent } from '@sbv-clients/util-misc';

import schema from '../schema';
import { useRegister } from '../utils/use-register';

type FormValues = InferType<typeof schema>;

interface RegisterResultContextValue {
  methods: UseFormReturn<FormValues>;
  handleSubmit: SubmitHandler<FormValues>;
}

const RegisterResultContext = createContext<
  RegisterResultContextValue | undefined
>(undefined);

export function useRegisterResult(): RegisterResultContextValue {
  return useContext(RegisterResultContext)!;
}

export const RegisterProvider: FunctionComponent = ({ children }) => {
  const register = useRegister();
  const [searchParams] = useSearchParams();
  const phoneNumber = searchParams.get('phoneNumber');

  const methods = useForm<FormValues>({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const handleSubmit = useCallback(
    async (formValues: FormValues) => {
      try {
        await register.mutateAsync({
          ...formValues,
          phoneNumber: phoneNumber || '',
        });
      } catch {
        /* empty */
      }
    },
    [phoneNumber, register]
  );

  const context: RegisterResultContextValue = useMemo(
    () => ({
      methods,
      handleSubmit,
    }),
    [handleSubmit, methods]
  );
  return (
    <RegisterResultContext.Provider value={context}>
      {children}
    </RegisterResultContext.Provider>
  );
};
